import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "@/i18n";
import { formatApiDate } from "../utils/dateLocal";
import { sharedTotals } from "../utils/listTotals";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

/**
 * SharedListPage - what someone outside the family sees.
 *
 * WHY THIS EXISTS (18 Sep 2026)
 *
 *     The Share button has worked for months: it creates a link and shows it
 *     to you to send. There was no page at the other end. The recipient hit
 *     the router's catch-all and landed on the home screen, with nothing to
 *     say why.
 *
 *     Found by a checker that asks which backend routes no frontend call can
 *     reach - GET /shared/{id} had no caller at all. The day before, the link
 *     itself had been fixed to carry the right public domain, mutation-tested
 *     four ways. The link was correct; the destination did not exist.
 *
 * WHAT IT SHOWS
 *     Everything about the shop - items, quantities, prices, stores, totals -
 *     because that is what Ivan decided a recipient should see.
 *
 *     The BACKEND decides what is sendable, not this page. It returns an
 *     allow-list, so a family member's phone number and the owner's account
 *     id stay out of the response entirely rather than merely going
 *     undisplayed. A share link is public and gets forwarded; anything in the
 *     response is readable whether or not it is rendered.
 *
 * NO SIGN-IN, DELIBERATELY
 *     The whole point is a person who does not have the app. This page is
 *     outside ProtectedRoute and asks for nothing.
 *
 * THREE STATES, NOT TWO
 *     Loading, loaded, and failed - and failed says which kind. An expired or
 *     revoked link is a different message from a connection problem, because
 *     the first is final and the second is worth retrying. Getting that wrong
 *     is the mistake this app made elsewhere all week: an absence displayed
 *     as a fact.
 */
export default function SharedListPage() {
  const { shareId } = useParams();
  const { t } = useTranslation();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/shared/${shareId}`)
      .then((r) => alive && setState({ status: "ok", data: r.data }))
      .catch((e) => {
        if (!alive) return;
        // 404 is the server saying the link is gone. Anything else is a
        // problem reaching it, which is worth trying again.
        const gone = e?.response?.status === 404;
        setState({ status: gone ? "gone" : "error" });
      });
    return () => { alive = false; };
  }, [shareId]);

  const money = (n, cur) =>
    typeof n === "number" ? `${cur || "A$"}${n.toFixed(2)}` : null;

  if (state.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-gray-400">
        {t("shared.loading")}
      </div>
    );
  }

  if (state.status !== "ok") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center gap-3">
        <p className="text-lg font-bold">
          {state.status === "gone" ? t("shared.gone") : t("shared.failed")}
        </p>
        <p className="text-sm text-gray-500 max-w-sm">
          {state.status === "gone"
            ? t("shared.goneDetail")
            : t("shared.failedDetail")}
        </p>
        {state.status === "error" && (
          <button
            type="button"
            data-testid="shared-retry-btn"
            onClick={() => { setState({ status: "loading" }); }}
            className="mt-2 px-4 py-2 rounded-full bg-black text-white text-sm"
          >
            {t("shared.retry")}
          </button>
        )}
      </div>
    );
  }

  const { list, family_name: familyName, expires_at: expiresAt } = state.data;
  const items = list?.items || [];
  const cur = list?.currency || "A$";

  // Grouped by the store each item is to be bought at, so a split shop reads
  // the way it does in the app.
  const groups = items.reduce((acc, it) => {
    const key = it.buy_at_store || list?.store || t("shared.noStore");
    (acc[key] = acc[key] || []).push(it);
    return acc;
  }, {});

  // The server's figures (V19.13, audit D18): this page used to add price x
  // quantity itself, so "500 g" of mince at A$4 read A$2,000 and unticked
  // items counted. Paid total is null while the list is still being shopped.
  const totals = sharedTotals(list);

  return (
    <div className="min-h-screen p-4 max-w-2xl mx-auto" data-testid="shared-list-page">
      <header className="mb-4">
        <p className="text-sm text-gray-500">{t("shared.sharedBy", { family: familyName })}</p>
        <h1 className="text-2xl font-bold break-words">{list?.name}</h1>
        {expiresAt && (
          <p className="text-xs text-gray-400 mt-1">
            {t("shared.expires", { date: formatApiDate(expiresAt) })}
          </p>
        )}
      </header>

      {items.length === 0 ? (
        <p className="text-gray-400">{t("shared.empty")}</p>
      ) : (
        Object.entries(groups).map(([store, rows]) => (
          <section key={store} className="mb-5">
            <h2 className="text-sm font-bold text-gray-500 mb-2">{store}</h2>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((it) => (
                <li key={it.id} className="py-2 flex items-start gap-3">
                  <span className={`flex-1 break-words ${it.checked ? "line-through text-gray-400" : ""}`}>
                    {/* A unit always shows, even for 1 ("1 pack tofu"), and so
                        does the recipe's amount (V19.18, ChatGPT deep QA D01). */}
                    {it.unit && it.quantity ? `${it.quantity} ${it.unit} ` : (it.quantity && it.quantity !== 1 ? `${it.quantity} ` : "")}
                    {it.name}
                    {it.recipe_amount && (
                      <span className="text-gray-400 text-xs ml-1">{t('listDetail.recipeAmount', { amount: it.recipe_amount })}</span>
                    )}
                  </span>
                  {money(totals.lineTotal(it), cur) && (
                    <span className="text-sm tabular-nums text-gray-600 dark:text-gray-300">
                      {money(totals.lineTotal(it), cur)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      <footer className="border-t pt-3 mt-4 space-y-1">
        <div className="flex justify-between text-sm text-gray-500">
          <span>{t("shared.itemsTotal")}</span>
          <span className="tabular-nums">{money(totals.itemsTotal, cur)}</span>
        </div>
        {typeof totals.paidTotal === "number" && (
          <div className="flex justify-between font-bold">
            <span>{t("shared.paidTotal")}</span>
            <span className="tabular-nums">{money(totals.paidTotal, cur)}</span>
          </div>
        )}
      </footer>
    </div>
  );
}
